import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  IconButton,
  Link,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from "@mui/material";
import { MRT_ColumnDef, MRT_ColumnFiltersState, MaterialReactTable } from "material-react-table";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import type { MouseEvent } from "react";
import { Helmet } from "react-helmet-async";
import Iconify from "../../minimals/components/iconify";
import CustomStatusFilter from "../orders/CustomMultiSelectFilter";
import { createAutocompleteColumn } from "../reports/generateAutocompleteColumnAndFilter";
import NoteCell from "../reports/NoteCell";
import OpenInHomePageButton from "../searches/OpenInHomePageButton";
import { exportTableToCsv } from "../taskUtils";
import { fShortMonthDayYearSlashNotation } from "../utils/formatTime";
import AddSurveyAndZoningModal from "./AddSurveyAndZoningModal";
import EditRequestedTaskEtaModal from "./EditRequestedTaskEtaModal";
import EditRequestedTaskStatusModal from "./EditRequestedTaskStatusModal";
import {
  buildEtaAccuracyRequestedNote,
  buildEtaConfirmedNote,
  filterSurveyZoningNotesForTask,
  getLatestOrderedDetails,
  getLatestClientChoiceDate,
  getLatestClientFollowUpDate,
  getLatestClientSignatureDate,
  getLatestSignatureReminderDate,
  getLatestProposalDate,
  getLatestProposalReminderDate,
  getLatestReceivedDate,
  getLatestSurveyZoningStatusNote,
  getLatestSurveyorContactedDate,
  getLatestQuoteWaitingDate,
  getLatestSurveyZoningEtaProgress,
  getLatestSurveyZoningEtaProgressDate,
  getLatestSurveyZoningStage,
  getLatestSurveyZoningStatusNoteDate,
  getLatestSurveyZoningStatus,
  getLatestSurveyZoningCompany,
  SurveyZoningOrderedDetails
} from "./orderedDetails";
import SurveyZoningActionButton from "./SurveyZoningActionButton";
import SurveyAndZoningNotesModal from "./SurveyAndZoningNotesModal";
import { RequestedTaskDescription, RequestedTaskStatusOption, SurveyAndZoning } from "./types";

const REFRESH_INTERVAL_MS = 60_000;
const SURVEY_ZONING_FILTERS_STORAGE_KEY = "surveyZoning_columnFilters";
const SURVEY_ZONING_FILTER_MODE_STORAGE_KEY = "surveyZoning_filterMode";
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const isSameDay = (a: Date, b: Date) => startOfDay(a).getTime() === startOfDay(b).getTime();
const getDaysFromToday = (value: string | Date) => {
  const parsed = new Date(value);
  const parsedStart = startOfDay(parsed).getTime();
  if (Number.isNaN(parsedStart)) {
    return null;
  }

  const todayStart = startOfDay(new Date()).getTime();
  return Math.max(0, Math.floor((todayStart - parsedStart) / MS_PER_DAY));
};

type SurveyZoningDisplayStatus =
  | "Waiting for quotes"
  | "Waiting on 1+ quote"
  | "Waiting on client choice"
  | "No response from client"
  | "Waiting on client signature"
  | "Client didn't sign"
  | "Waiting on proposal"
  | "Proposal not received"
  | "Surveyor contacted"
  | "Quote not received"
  | "1+ quote not received"
  | "Ordered"
  | "ETA Upcoming"
  | "ETA Accuracy Requested"
  | "ETA Confirmed"
  | "Due"
  | "Overdue"
  | "Received"
  | "Received, need invoice"
  | "N/A";

type SurveyZoningFilterMode = "default" | "remembered";

const statusOptions: RequestedTaskStatusOption[] = [
  { fieldDisplayName: "Waiting for quotes", value: "Waiting for quotes" },
  { fieldDisplayName: "Waiting on 1+ quote", value: "Waiting on 1+ quote" },
  { fieldDisplayName: "Waiting on client choice", value: "Waiting on client choice" },
  { fieldDisplayName: "Waiting on client signature", value: "Waiting on client signature" },
  { fieldDisplayName: "Waiting on proposal", value: "Waiting on proposal" },
  { fieldDisplayName: "Surveyor contacted", value: "Surveyor contacted" },
  { fieldDisplayName: "Due", value: "Due" },
  { fieldDisplayName: "Ordered", value: "Ordered" },
  { fieldDisplayName: "Received", value: "Received" },
  { fieldDisplayName: "Received, need invoice", value: "Received, need invoice" },
  { fieldDisplayName: "N/A", value: "NA" }
];

// Keep the default view focused on items that currently need attention. The
// complete status list remains available in the status filter.
const defaultActionableStatuses: SurveyZoningDisplayStatus[] = [
  "Due",
  "Overdue",
  "ETA Upcoming",
  "ETA Accuracy Requested",
  "Quote not received",
  "1+ quote not received",
  "No response from client",
  "Client didn't sign",
  "Proposal not received",
  "Surveyor contacted",
  "Received, need invoice"
];
const defaultColumnFilters: MRT_ColumnFiltersState = [
  { id: "status", value: defaultActionableStatuses },
  { id: "state", value: ["NY", "TX"] }
];

const areFiltersEqual = (first: MRT_ColumnFiltersState, second: MRT_ColumnFiltersState) =>
  first.length === second.length &&
  first.every((firstFilter) =>
    second.some(
      (secondFilter) =>
        firstFilter.id === secondFilter.id && JSON.stringify(firstFilter.value) === JSON.stringify(secondFilter.value)
    )
  );

const surveyOnlyStatusLabels = new Set([
  "Waiting on 1+ quote",
  "Waiting on client choice",
  "Waiting on client signature",
  "Waiting on proposal",
  "Surveyor contacted",
  "Due",
  "Quote not received",
  "1+ quote not received",
  "No response from client",
  "Client didn't sign",
  "Proposal not received",
  "Received, need invoice"
]);

const getStatusOptionsForTask = (taskDescriptions: RequestedTaskDescription[]) =>
  taskDescriptions.includes("Survey")
    ? statusOptions
    : statusOptions.filter((option) => !surveyOnlyStatusLabels.has(option.fieldDisplayName));

type EditableRequestedTaskStatus = {
  currentContactEmail?: string | null;
  currentContactName?: string | null;
  currentEta?: string | null;
  currentStatus?: string | null;
  currentCompany?: string | null;
  currentStatusNote?: string | null;
  currentDisplayStatus?: SurveyZoningDisplayStatus | null;
  existingOrderedDetails?: SurveyZoningOrderedDetails | null;
  quoteRequestedAt?: string | Date | null;
  quoteWaitingStatus?: "Waiting for quotes" | "Waiting on 1+ quote" | null;
  clientChoiceRequestedAt?: string | Date | null;
  clientFollowUpAt?: string | Date | null;
  signatureRequestedAt?: string | Date | null;
  signatureReminderAt?: string | Date | null;
  proposalRequestedAt?: string | Date | null;
  proposalReminderAt?: string | Date | null;
  receivedDate?: string | null;
  orderNumber: string;
  orderNumberDisplay: string;
  taskDescriptions: RequestedTaskDescription[];
} | null;

type EditableRequestedTaskEta = {
  currentEta?: string | null;
  orderNumber: string;
  orderNumberDisplay: string;
  taskDescriptions: RequestedTaskDescription[];
} | null;

const getRequestedTaskStatusColor = (status?: string | null) => {
  switch (status) {
    case "Waiting for quotes":
      return "warning";
    case "Ordered":
      return "warning";
    case "Quote not received":
    case "1+ quote not received":
      return "error";
    case "No response from client":
      return "error";
    case "ETA Upcoming":
    case "ETA Accuracy Requested":
    case "ETA Confirmed":
      return "primary";
    case "Required":
      return "error";
    case "Due":
    case "Overdue":
      return "error";
    case "Received":
    case "Received, need invoice":
      return "success";
    case "N/A":
      return "default";
    default:
      return "warning";
  }
};

const getEditableStatus = (row: SurveyAndZoning): SurveyZoningDisplayStatus | null => {
  const status = row.requestedTaskStatus?.trim();
  const latestStage = getLatestSurveyZoningStage(row.notes);

  if (!status) {
    if (latestStage === "ordered") return "Ordered";
    if (latestStage === "waiting_on_client_choice") return "Waiting on client choice";
    if (latestStage === "followed_up_with_client") return "Waiting on client choice";
    if (latestStage === "waiting_on_client_signature" || latestStage === "signature_reminded") {
      return "Waiting on client signature";
    }
    if (latestStage === "waiting_on_proposal" || latestStage === "proposal_reminded") {
      return "Waiting on proposal";
    }
    if (latestStage === "surveyor_contacted") return "Ordered";
    if (latestStage === "waiting_on_one_or_more_quotes") return "Waiting on 1+ quote";
    if (latestStage === "waiting_for_quotes") return "Waiting for quotes";
    return null;
  }

  if (status === "Requested") {
    return "Ordered";
  }

  if (status === "Required") {
    if (latestStage === "waiting_on_client_choice") return "Waiting on client choice";
    if (latestStage === "followed_up_with_client") return "Waiting on client choice";
    if (latestStage === "waiting_on_client_signature" || latestStage === "signature_reminded") {
      return "Waiting on client signature";
    }
    if (latestStage === "waiting_on_proposal" || latestStage === "proposal_reminded") {
      return "Waiting on proposal";
    }
    if (latestStage === "surveyor_contacted") return "Ordered";
    if (latestStage === "waiting_on_one_or_more_quotes") return "Waiting on 1+ quote";
    return "Waiting for quotes";
  }

  const latestTrackedStatus = getLatestSurveyZoningStatus(row.notes);
  if (latestTrackedStatus === "Received, need invoice" && status === "Received") {
    return "Received, need invoice";
  }

  if (status === "Received" || status === "N/A") {
    return status;
  }

  if (latestStage === "ordered" && status !== "Received" && status !== "N/A") {
    return "Ordered";
  }

  if (latestStage === "waiting_for_quotes" && status !== "Received" && status !== "N/A") {
    return "Waiting for quotes";
  }

  if (latestStage === "waiting_on_one_or_more_quotes" && status !== "Received" && status !== "N/A") {
    return "Waiting on 1+ quote";
  }

  if (latestStage === "waiting_on_client_choice" && status !== "Received" && status !== "N/A") {
    return "Waiting on client choice";
  }

  if (latestStage === "followed_up_with_client" && status !== "Received" && status !== "N/A") {
    return "Waiting on client choice";
  }

  if (
    (latestStage === "waiting_on_client_signature" || latestStage === "signature_reminded") &&
    status !== "Received" &&
    status !== "N/A"
  ) {
    return "Waiting on client signature";
  }

  if (
    (latestStage === "waiting_on_proposal" || latestStage === "proposal_reminded") &&
    status !== "Received" &&
    status !== "N/A"
  ) {
    return "Waiting on proposal";
  }

  if (latestStage === "surveyor_contacted" && status !== "Received" && status !== "N/A") {
    return "Ordered";
  }

  return null;
};

const getDisplayStatus = (row: SurveyAndZoning): SurveyZoningDisplayStatus | null => {
  const editableStatus = getEditableStatus(row);
  if (editableStatus !== "Ordered") {
    if (row.requestType === "Survey" && editableStatus === "Waiting on client signature") {
      const latestStage = getLatestSurveyZoningStage(row.notes);
      const waitingSince =
        latestStage === "signature_reminded"
          ? getLatestSignatureReminderDate(row.notes)
          : getLatestClientSignatureDate(row.notes);
      if (waitingSince) {
        const waitingDate = new Date(waitingSince);
        const nextOneAm = new Date(waitingDate);
        nextOneAm.setHours(1, 0, 0, 0);
        if (waitingDate >= nextOneAm) nextOneAm.setDate(nextOneAm.getDate() + 1);
        const nextFourPm = new Date(waitingDate);
        nextFourPm.setHours(16, 0, 0, 0);
        if (waitingDate >= nextFourPm) nextFourPm.setDate(nextFourPm.getDate() + 1);
        if (Date.now() >= Math.min(nextOneAm.getTime(), nextFourPm.getTime())) {
          return "Client didn't sign";
        }
      }
    }
    if (row.requestType === "Survey" && editableStatus === "Waiting on proposal") {
      const latestStage = getLatestSurveyZoningStage(row.notes);
      const waitingSince =
        latestStage === "proposal_reminded" ? getLatestProposalReminderDate(row.notes) : getLatestProposalDate(row.notes);
      if (waitingSince) {
        const waitingDate = new Date(waitingSince);
        const oneAm = new Date(waitingDate);
        oneAm.setHours(1, 0, 0, 0);
        if (waitingDate >= oneAm) oneAm.setDate(oneAm.getDate() + 1);
        const fourPm = new Date(waitingDate);
        fourPm.setHours(16, 0, 0, 0);
        if (waitingDate >= fourPm) fourPm.setDate(fourPm.getDate() + 1);
        if (Date.now() >= Math.min(oneAm.getTime(), fourPm.getTime())) return "Proposal not received";
      }
    }
    if (row.requestType === "Survey" && editableStatus === "Waiting on client choice") {
      const latestStage = getLatestSurveyZoningStage(row.notes);
      const waitingSince =
        latestStage === "followed_up_with_client"
          ? getLatestClientFollowUpDate(row.notes)
          : getLatestClientChoiceDate(row.notes);
      const waitingHours = latestStage === "followed_up_with_client" ? 72 : 48;
      if (waitingSince && new Date(waitingSince).getTime() <= Date.now() - waitingHours * 60 * 60 * 1000) {
        return "No response from client";
      }
    }
    if (row.requestType === "Survey" && (editableStatus === "Waiting for quotes" || editableStatus === "Waiting on 1+ quote")) {
      const waitingDate = getLatestQuoteWaitingDate(row.notes) || row.requestedTaskLastModifiedOn;
      if (waitingDate && new Date(waitingDate).getTime() <= Date.now() - 24 * 60 * 60 * 1000) {
        return editableStatus === "Waiting for quotes" ? "Quote not received" : "1+ quote not received";
      }
    }
    return editableStatus;
  }

  const etaValue = row.requestedTaskDueDate;
  if (!etaValue) {
    return "Ordered";
  }

  const etaDate = startOfDay(new Date(etaValue));
  if (Number.isNaN(etaDate.getTime())) {
    return "Ordered";
  }

  const latestStage = getLatestSurveyZoningStage(row.notes);
  const surveyorContactedAt = getLatestSurveyorContactedDate(row.notes);
  if (
    row.requestType === "Survey" &&
    latestStage === "surveyor_contacted" &&
    surveyorContactedAt &&
    new Date(surveyorContactedAt).getTime() > Date.now() - 60 * 60 * 1000
  ) {
    return "Surveyor contacted";
  }

  const todayStart = startOfDay(new Date());
  if (etaDate < todayStart) {
    return "Overdue";
  }

  if (isSameDay(etaDate, todayStart)) {
    return "Due";
  }

  const latestEtaProgress = getLatestSurveyZoningEtaProgress(row.notes);
  if (latestEtaProgress === "eta_confirmed") {
    return "ETA Confirmed";
  }

  if (latestEtaProgress === "eta_accuracy_requested") {
    return "ETA Accuracy Requested";
  }

  const plus3DaysStart = new Date(todayStart);
  plus3DaysStart.setDate(plus3DaysStart.getDate() + 2);

  if (etaDate <= plus3DaysStart) {
    return "ETA Upcoming";
  }

  return "Ordered";
};

const getStatusAction = (row: SurveyAndZoning) => {
  if (getEditableStatus(row) !== "Ordered") {
    return null;
  }

  const etaValue = row.requestedTaskDueDate;
  if (!etaValue) {
    return null;
  }

  const etaDate = startOfDay(new Date(etaValue));
  if (Number.isNaN(etaDate.getTime())) {
    return null;
  }

  const todayStart = startOfDay(new Date());
  if (etaDate <= todayStart) {
    return null;
  }

  const latestEtaProgress = getLatestSurveyZoningEtaProgress(row.notes);
  if (latestEtaProgress === "eta_confirmed") {
    return null;
  }

  if (latestEtaProgress === "eta_accuracy_requested") {
    return { actionLabel: "Confirmed" as const, noteText: buildEtaConfirmedNote() };
  }

  const plus3DaysStart = new Date(todayStart);
  plus3DaysStart.setDate(plus3DaysStart.getDate() + 2);

  if (etaDate <= plus3DaysStart) {
    return { actionLabel: "Requested" as const, noteText: buildEtaAccuracyRequestedNote() };
  }

  return null;
};

export default function SurveyZoning() {
  const [isLoading, setIsLoading] = useState(true);
  const [rows, setRows] = useState<SurveyAndZoning[]>([]);
  const [loadError, setLoadError] = useState("");
  const [filterMode, setFilterMode] = useState<SurveyZoningFilterMode>("default");
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(defaultColumnFilters);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedEditStatusTask, setSelectedEditStatusTask] = useState<EditableRequestedTaskStatus>(null);
  const [selectedEditEtaTask, setSelectedEditEtaTask] = useState<EditableRequestedTaskEta>(null);
  const [selectedNotesRow, setSelectedNotesRow] = useState<SurveyAndZoning | null>(null);

  const fetchData = async (showLoader = true) => {
    if (showLoader) {
      setIsLoading(true);
    }

    try {
      const { data } = await axios.get<SurveyAndZoning[]>("/api/outstandingchecks/SurveyAndZoning");
      setRows(data);
      setLoadError("");
    } catch (error) {
      if (axios.isAxiosError(error)) {
        setLoadError(
          typeof error.response?.data === "string"
            ? error.response.data
            : error.response?.data?.message || "Unable to load survey and zoning."
        );
      } else {
        setLoadError("Unable to load survey and zoning.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData(true);
    const intervalId = window.setInterval(() => fetchData(false), REFRESH_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem(SURVEY_ZONING_FILTER_MODE_STORAGE_KEY, filterMode);
    if (filterMode === "remembered") {
      localStorage.setItem(SURVEY_ZONING_FILTERS_STORAGE_KEY, JSON.stringify(columnFilters));
    }
  }, [columnFilters, filterMode]);

  const handleColumnFiltersChange = (
    updaterOrValue: MRT_ColumnFiltersState | ((prev: MRT_ColumnFiltersState) => MRT_ColumnFiltersState)
  ) => {
    setColumnFilters((previousFilters) => {
      const nextFilters = typeof updaterOrValue === "function" ? updaterOrValue(previousFilters) : updaterOrValue;
      if (filterMode === "default" && !areFiltersEqual(nextFilters, defaultColumnFilters)) {
        setFilterMode("remembered");
      }
      return nextFilters;
    });
  };

  const handleFilterModeChange = (_event: MouseEvent<HTMLElement>, nextMode: SurveyZoningFilterMode | null) => {
    if (!nextMode || nextMode === filterMode) return;

    setFilterMode(nextMode);
    if (nextMode === "default") {
      setColumnFilters(defaultColumnFilters);
    } else {
      const savedFilters = localStorage.getItem(SURVEY_ZONING_FILTERS_STORAGE_KEY);
      if (savedFilters) {
        try {
          setColumnFilters(JSON.parse(savedFilters) as MRT_ColumnFiltersState);
          return;
        } catch {
          // Fall back to the current filters when stored filters are invalid.
        }
      }
    }
  };

  const displayRows = useMemo(
    () =>
      rows.flatMap((row) => {
        const taskRows: SurveyAndZoning[] = [];

        if (row.needsSurvey) {
          taskRows.push({
            ...row,
            rowId: `${row.orderNumber}:Survey`,
            requestType: "Survey",
            taskDescriptions: ["Survey"],
            requestedTaskStatus: row.surveyRequestedTaskStatus,
            requestedTaskDueDate: row.surveyRequestedTaskDueDate,
            requestedTaskLastModifiedOn: row.surveyRequestedTaskLastModifiedOn,
            contactName: row.surveyContactName,
            contactEmail: row.surveyContactEmail,
            notes: filterSurveyZoningNotesForTask(row.notes, "Survey")
          });
        }

        if (row.needsZoningReport) {
          taskRows.push({
            ...row,
            rowId: `${row.orderNumber}:Zoning Report`,
            requestType: "Zoning Report",
            taskDescriptions: ["Zoning Report"],
            requestedTaskStatus: row.zoningRequestedTaskStatus,
            requestedTaskDueDate: row.zoningRequestedTaskDueDate,
            requestedTaskLastModifiedOn: row.zoningRequestedTaskLastModifiedOn,
            contactName: row.zoningContactName,
            contactEmail: row.zoningContactEmail,
            notes: filterSurveyZoningNotesForTask(row.notes, "Zoning Report")
          });
        }

        return taskRows;
      }),
    [rows]
  );

  const getRequestedItems = (row: SurveyAndZoning): RequestedTaskDescription[] => {
    if (row.taskDescriptions?.length) {
      return row.taskDescriptions;
    }

    const items: RequestedTaskDescription[] = [];
    if (row.needsSurvey) items.push((row.surveyRequestedTaskDescription as RequestedTaskDescription) || "Survey");
    if (row.needsZoningReport) {
      items.push((row.zoningRequestedTaskDescription as RequestedTaskDescription) || "Zoning Report");
    }
    return items;
  };

  const getDisplayEta = (row: SurveyAndZoning) => {
    const etaValue = row.requestedTaskDueDate;

    if (!etaValue) {
      return { display: "-", value: null as string | null };
    }

    return {
      display: fShortMonthDayYearSlashNotation(etaValue),
      value: etaValue
    };
  };

  const getDaysSinceLastStatus = (row: SurveyAndZoning) => {
    const latestTrackedStatusDate = [
      getLatestSurveyZoningStatusNoteDate(row.notes),
      getLatestQuoteWaitingDate(row.notes),
      getLatestClientChoiceDate(row.notes),
      getLatestClientFollowUpDate(row.notes),
      getLatestClientSignatureDate(row.notes),
      getLatestSignatureReminderDate(row.notes),
      getLatestProposalDate(row.notes),
      getLatestProposalReminderDate(row.notes),
      getLatestSurveyorContactedDate(row.notes),
      getLatestSurveyZoningEtaProgressDate(row.notes)
    ]
      .filter(Boolean)
      .map((value) => new Date(value as string | Date).getTime())
      .filter((value) => !Number.isNaN(value));

    const timestamps = latestTrackedStatusDate.length
      ? latestTrackedStatusDate
      : [row.requestedTaskLastModifiedOn]
          .filter(Boolean)
          .map((value) => new Date(value as string | Date).getTime())
          .filter((value) => !Number.isNaN(value));

    if (!timestamps.length) {
      return null;
    }

    const latestTimestamp = Math.max(...timestamps);
    return getDaysFromToday(new Date(latestTimestamp));
  };

  const getContactSummary = (row: SurveyAndZoning) => {
    const orderedDetails = getLatestOrderedDetails(row.notes);
    if (orderedDetails?.contactName && getLatestSurveyZoningStage(row.notes) !== "waiting_for_quotes") {
      return {
        display: orderedDetails.contactName,
        email: orderedDetails.contactEmail || row.contactEmail?.trim() || ""
      };
    }

    const name = row.contactName?.trim() || row.contactEmail?.trim() || "";
    const email = row.contactEmail?.trim() || "";

    if (!name && !email) {
      return { display: "-", email: "" };
    }

    return {
      display: name || email,
      email
    };
  };

  const renderStatusCell = (row: SurveyAndZoning) => {
    const displayStatus = getDisplayStatus(row);
    const editableStatus = getEditableStatus(row);
    const statusAction = getStatusAction(row);
    const orderedCompany = getLatestOrderedDetails(row.notes)?.contactName;
    const companyStatus = editableStatus && ["Waiting for quotes", "Waiting on 1+ quote", "Waiting on client signature", "Waiting on proposal"].includes(editableStatus)
      ? editableStatus as "Waiting for quotes" | "Waiting on 1+ quote" | "Waiting on client signature" | "Waiting on proposal"
      : null;
    const statusCompany = companyStatus ? getLatestSurveyZoningCompany(row.notes, companyStatus) : orderedCompany;
    const statusNote = displayStatus === "Overdue" ? getLatestSurveyZoningStatusNote(row.notes) : null;
    const statusTooltip = statusNote ? `Note: ${statusNote}` : statusCompany ? `Company: ${statusCompany}` : "";

    if (!displayStatus) {
      return <Typography>-</Typography>;
    }

    return (
      <Stack direction="row" alignItems="center" spacing={1}>
        <IconButton
          onClick={() =>
            setSelectedEditStatusTask({
              currentContactEmail: row.contactEmail,
              currentContactName: row.contactName,
              currentEta: getDisplayEta(row).value,
              currentStatus: editableStatus,
              currentCompany: statusCompany,
              currentStatusNote: statusNote,
              currentDisplayStatus: displayStatus,
              existingOrderedDetails: getLatestOrderedDetails(row.notes),
              quoteRequestedAt: getLatestQuoteWaitingDate(row.notes) || row.requestedTaskLastModifiedOn,
              quoteWaitingStatus:
                editableStatus === "Waiting for quotes" || editableStatus === "Waiting on 1+ quote"
                  ? editableStatus
                  : null,
              clientChoiceRequestedAt: getLatestClientChoiceDate(row.notes),
              clientFollowUpAt: getLatestClientFollowUpDate(row.notes),
              signatureRequestedAt: getLatestClientSignatureDate(row.notes),
              signatureReminderAt: getLatestSignatureReminderDate(row.notes),
              proposalRequestedAt: getLatestProposalDate(row.notes),
              proposalReminderAt: getLatestProposalReminderDate(row.notes),
              receivedDate: getLatestReceivedDate(row.notes),
              orderNumber: row.orderNumber,
              orderNumberDisplay: row.orderNumberWithPrefix,
              taskDescriptions: getRequestedItems(row)
            })
          }
        >
          <Iconify icon="eva:edit-fill" />
        </IconButton>
        <Tooltip title={statusTooltip} arrow placement="top">
          <Chip
            label={displayStatus}
            size="small"
            color={getRequestedTaskStatusColor(displayStatus)}
            sx={{ borderRadius: 1 }}
            variant="soft"
          />
        </Tooltip>
        {statusAction && row.requestType && (
          <SurveyZoningActionButton
            actionLabel={statusAction.actionLabel}
            noteText={statusAction.noteText}
            onError={setLoadError}
            onSaved={() => fetchData(false)}
            orderNumber={row.orderNumber}
            taskDescription={row.requestType}
          />
        )}
      </Stack>
    );
  };

  const renderEtaCell = (row: SurveyAndZoning) => {
    const eta = getDisplayEta(row);

    return (
      <Stack direction="row" alignItems="center" spacing={1}>
        <IconButton
          onClick={() =>
            setSelectedEditEtaTask({
              currentEta: eta.value,
              orderNumber: row.orderNumber,
              orderNumberDisplay: row.orderNumberWithPrefix,
              taskDescriptions: getRequestedItems(row)
            })
          }
        >
          <Iconify icon="eva:edit-fill" />
        </IconButton>
        <Typography>{eta.display}</Typography>
      </Stack>
    );
  };

  const columns = useMemo<MRT_ColumnDef<SurveyAndZoning>[]>(
    () => [
      {
        header: "Order Number",
        accessorKey: "orderNumberWithPrefix",
        Cell: ({ renderedCellValue, row }) => (
          <Stack direction="row" spacing={1} alignItems="center">
            <OpenInHomePageButton orderNumber={row.original.orderNumber} />
            <Link href={`/searches/${row.original.orderNumber}`} target="_blank">
              {renderedCellValue}
            </Link>
          </Stack>
        )
      },
      {
        header: "Type",
        id: "requestType",
        accessorFn: (row) => row.requestType || "-"
      },
      {
        header: "Exclude States",
        accessorKey: "state",
        filterVariant: "multi-select",
        filterFn: (row, id, value) => {
          const excludedStates = Array.isArray(value) ? value : [];
          return !excludedStates.includes(row.getValue<string | null>(id) || "");
        },
        filterSelectOptions: ["NY", "TX"]
      },
      {
        header: "Status",
        id: "status",
        accessorFn: (row) => getDisplayStatus(row) || "-",
        Cell: ({ row }) => renderStatusCell(row.original),
        filterVariant: "multi-select",
        filterSelectOptions: [
          { text: "Due", value: "Due" },
          { text: "Overdue", value: "Overdue" },
          { text: "Ordered", value: "Ordered" },
          { text: "ETA Upcoming", value: "ETA Upcoming" },
          { text: "ETA Accuracy Requested", value: "ETA Accuracy Requested" },
          { text: "ETA Confirmed", value: "ETA Confirmed" },
          { text: "Received", value: "Received" },
          { text: "Received, need invoice", value: "Received, need invoice" },
          { text: "Waiting for quotes", value: "Waiting for quotes" },
          { text: "Quote not received", value: "Quote not received" },
          { text: "1+ quote not received", value: "1+ quote not received" },
          { text: "Waiting on client choice", value: "Waiting on client choice" },
          { text: "No response from client", value: "No response from client" },
          { text: "Waiting on client signature", value: "Waiting on client signature" },
          { text: "Client didn't sign", value: "Client didn't sign" },
          { text: "Waiting on proposal", value: "Waiting on proposal" },
          { text: "Proposal not received", value: "Proposal not received" },
          { text: "Surveyor contacted", value: "Surveyor contacted" },
          { text: "N/A", value: "N/A" }
        ],
        muiFilterTextFieldProps: {
          variant: "outlined",
          size: "small"
        },
        Filter: CustomStatusFilter
      },
      createAutocompleteColumn<SurveyAndZoning>("Department", "department", {
        placeholder: "Department",
        cellPaddingRight: 3,
        size: 150
      }),
      {
        header: "Days Since Last Status",
        id: "daysSinceLastStatus",
        accessorFn: (row) => getDaysSinceLastStatus(row) ?? "",
        Cell: ({ row }) => {
          const daysSinceLastStatus = getDaysSinceLastStatus(row.original);
          return daysSinceLastStatus === null ? <Typography>-</Typography> : <Chip label={daysSinceLastStatus} size="small" />;
        }
      },
      {
        header: "ETA",
        id: "eta",
        accessorFn: (row) => getDisplayEta(row).display,
        Cell: ({ row }) => renderEtaCell(row.original)
      },
      {
        header: "Received Date",
        id: "receivedDate",
        accessorFn: (row) => {
          const receivedDate = getLatestReceivedDate(row.notes);
          return receivedDate ? fShortMonthDayYearSlashNotation(receivedDate) : "";
        },
        Cell: ({ row }) => {
          const receivedDate = getLatestReceivedDate(row.original.notes);
          return <Typography>{receivedDate ? fShortMonthDayYearSlashNotation(receivedDate) : "-"}</Typography>;
        }
      },
      {
        header: "Surveyor / Zoning Co",
        id: "contact",
        accessorFn: (row) => getContactSummary(row).display,
        Cell: ({ row }) => {
          const contact = getContactSummary(row.original);
          return contact.email ? <Link href={`mailto:${contact.email}`}>{contact.display}</Link> : <Typography>{contact.display}</Typography>;
        }
      },
      {
        header: "Closer",
        accessorKey: "closer",
        Cell: ({ renderedCellValue, row }) =>
          row.original.closerEmail ? (
            <Link href={`mailto:${row.original.closerEmail}`}>{renderedCellValue || "-"}</Link>
          ) : (
            <Typography>{renderedCellValue || "-"}</Typography>
          )
      },
      {
        id: "notes",
        header: "Notes",
        enableColumnFilter: false,
        enableColumnActions: false,
        enableColumnDragging: false,
        enableColumnFilterModes: false,
        size: 80,
        Cell: ({ row }) => <NoteCell row={row.original} onNoteClick={setSelectedNotesRow} color="secondary" />
      }
    ],
    []
  );

  return (
    <>
      <Helmet>
        <title>Survey & Zoning | TitleQ</title>
      </Helmet>
      <Container maxWidth="xl">
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="h4" marginBottom={2}>
            Survey & Zoning
          </Typography>
          <Button
            color="primary"
            variant="contained"
            startIcon={<Iconify icon="eva:plus-fill" />}
            onClick={() => setIsAddModalOpen(true)}
            size="medium"
          >
            Add Order
          </Button>
        </Stack>
        {loadError && (
          <Alert severity="error" sx={{ marginBottom: 2 }}>
            {loadError}
          </Alert>
        )}
        <MaterialReactTable
          enableStickyHeader
          columns={columns}
          data={displayRows}
          enableGlobalFilter
          enableFacetedValues
          autoResetPageIndex={false}
          globalFilterFn="includesString"
          getRowId={(row) => row.rowId || `${row.orderNumber}:${row.requestType || "row"}`}
          state={{
            showSkeletons: isLoading,
            showColumnFilters: true,
            columnFilters
          }}
          onColumnFiltersChange={handleColumnFiltersChange}
          initialState={{
            showColumnFilters: true,
            density: "compact",
            pagination: { pageIndex: 0, pageSize: 30 }
          }}
          renderTopToolbarCustomActions={({ table }) => (
            <Box sx={{ flexGrow: 1, position: "relative" }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ position: "absolute", left: 0 }}>
                <Typography variant="body2" color="text.secondary">
                  Filter mode:
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  color="primary"
                  size="small"
                  value={filterMode}
                  onChange={handleFilterModeChange}
                  aria-label="Survey and zoning filter mode"
                >
                  <ToggleButton value="default">Default</ToggleButton>
                  <ToggleButton value="remembered">Remembered</ToggleButton>
                </ToggleButtonGroup>
              </Stack>
              <Box sx={{ position: "absolute", right: 0 }}>
                <Tooltip title="Export to csv">
                  <IconButton onClick={() => exportTableToCsv(table, "survey-zoning.csv")}>
                    <Iconify icon="eva:download-fill" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          )}
        />
      </Container>
      <AddSurveyAndZoningModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSaved={() => fetchData(true)}
      />
      {selectedEditStatusTask && (
        <EditRequestedTaskStatusModal
          currentContactEmail={selectedEditStatusTask.currentContactEmail}
          currentContactName={selectedEditStatusTask.currentContactName}
          currentEta={selectedEditStatusTask.currentEta}
          currentStatus={selectedEditStatusTask.currentStatus}
          currentCompany={selectedEditStatusTask.currentCompany}
          currentStatusNote={selectedEditStatusTask.currentStatusNote}
          currentDisplayStatus={selectedEditStatusTask.currentDisplayStatus}
          existingOrderedDetails={selectedEditStatusTask.existingOrderedDetails}
          quoteRequestedAt={selectedEditStatusTask.quoteRequestedAt}
          quoteWaitingStatus={selectedEditStatusTask.quoteWaitingStatus}
          clientChoiceRequestedAt={selectedEditStatusTask.clientChoiceRequestedAt}
          clientFollowUpAt={selectedEditStatusTask.clientFollowUpAt}
          signatureRequestedAt={selectedEditStatusTask.signatureRequestedAt}
          signatureReminderAt={selectedEditStatusTask.signatureReminderAt}
          proposalRequestedAt={selectedEditStatusTask.proposalRequestedAt}
          proposalReminderAt={selectedEditStatusTask.proposalReminderAt}
          receivedDate={selectedEditStatusTask.receivedDate}
          isOpen={true}
          onClose={() => setSelectedEditStatusTask(null)}
          onSaved={() => fetchData(true)}
          orderNumber={selectedEditStatusTask.orderNumber}
          orderNumberDisplay={selectedEditStatusTask.orderNumberDisplay}
          statusOptions={getStatusOptionsForTask(selectedEditStatusTask.taskDescriptions)}
          taskDescriptions={selectedEditStatusTask.taskDescriptions}
        />
      )}
      {selectedEditEtaTask && (
        <EditRequestedTaskEtaModal
          currentEta={selectedEditEtaTask.currentEta}
          isOpen={true}
          onClose={() => setSelectedEditEtaTask(null)}
          onSaved={() => fetchData(true)}
          orderNumber={selectedEditEtaTask.orderNumber}
          orderNumberDisplay={selectedEditEtaTask.orderNumberDisplay}
          taskDescriptions={selectedEditEtaTask.taskDescriptions}
        />
      )}
      {selectedNotesRow && (
        <SurveyAndZoningNotesModal
          entity={selectedNotesRow}
          isOpen={true}
          orderNumberDisplay={selectedNotesRow.orderNumberWithPrefix}
          setEntity={setSelectedNotesRow}
          setRows={setRows}
          taskDescriptions={getRequestedItems(selectedNotesRow)}
        />
      )}
    </>
  );
}
